import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateTimeType,
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  TypeGuard,
  isISODateTimeType,
  serialise,
  toJSON,
  toTimestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import {
  BaseSourceEntity,
  TRANSFORM_DATA_FN_DEFAULTS,
} from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import { createHash } from 'crypto';

export const PATIENT_APPOINTMENT_DETAIL_RESOURCE_TYPE =
  'patientAppointmentDetails';

export const PATIENT_APPOINTMENT_DETAIL_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Appointment Detail List',
      description: '',
      idPrefix: PATIENT_APPOINTMENT_DETAIL_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export enum CorePracticePatientAppointmentDetailStatus {
  Tentative = 'Tentative',
  Confirmed = 'Confirmed',
  Cancelled = 'Cancelled',
}

export enum CorePracticePatientAppointmentDetailAttendance {
  FailedToAttend = 'FailedToAttend',
  UnableToAttend = 'UnableToAttend',
  Cancelled = 'Cancelled',
  Pending = 'Pending',
  Attended = 'Attended',
}

export interface ICorePracticePatientAppointmentDetail {
  patientId: number;
  calendarId: number;
  startTime: ISODateTimeType;
  endTime: ISODateTimeType;
  duration: number;
  status: CorePracticePatientAppointmentDetailStatus;
  attendance: CorePracticePatientAppointmentDetailAttendance;
  appointmentType?: string;
  appointmentNotes?: string;
  tags?: string[];
}

export function isCorePracticePatientAppointmentDetail(
  item: unknown,
  logError: boolean
): item is ICorePracticePatientAppointmentDetail {
  return TypeGuard.interface<ICorePracticePatientAppointmentDetail>(
    {
      patientId: isNumber,
      calendarId: isNumber,
      startTime: isISODateTimeType,
      endTime: isISODateTimeType,
      duration: isNumber,
      status: TypeGuard.enumValue(CorePracticePatientAppointmentDetailStatus),
      attendance: TypeGuard.enumValue(
        CorePracticePatientAppointmentDetailAttendance
      ),
      appointmentType: TypeGuard.nilOr(isString),
      appointmentNotes: TypeGuard.nilOr(isString),
      tags: TypeGuard.nilOr(TypeGuard.arrayOf(isString)),
    },
    logError
  )(item);
}

export interface ICorePracticePatientAppointmentDetailTranslations {
  from: Timestamp;
  to: Timestamp;
}

export interface ICorePracticePatientAppointmentDetailFilters {
  patientId: number;
}

const PATIENT_APPOINTMENT_DETAIL_SOURCE_QUERY = `
WITH patient AS MATERIALIZED (
  SELECT
    patient_no::text AS patient_number,
    MIN(id) AS id
  FROM (
    SELECT PatientId AS id, PatientNo AS patient_no FROM tblPatient
  ) AS all_patients
  GROUP BY patient_no::text
),
calendar AS MATERIALIZED (
  SELECT
    Description AS description,
    MIN(CalendarId) AS id
  FROM tblCalendar
  GROUP BY Description
)
SELECT
  patient.id AS patient_id,
  calendar.id AS calendar_id,
  detail.start_time,
  detail.end_time,
  detail.duration,
  detail.status,
  detail.attendance,
  detail.appointment_type,
  detail.appointment_notes,
  detail.tags
FROM
(
  SELECT
    calendar AS calendar_label,
    to_char(
      convert_to_date(date, 'DD/Mon/YYYY')
      +
      to_timestamp(starttime, 'HH24:MI')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS start_time,
    to_char(
      convert_to_date(date, 'DD/Mon/YYYY')
      +
      to_timestamp(endtime, 'HH24:MI')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS end_time,
    dur AS duration,
    status,
    attendance,
    patient_no::text AS patient_number,
    NULLIF(reason, '') AS appointment_type,
    NULLIF(description, '') AS appointment_notes,
    NULLIF(tags, '') AS tags
  FROM AppointmentDetail
) as detail
JOIN patient
  ON patient.patient_number = detail.patient_number
JOIN calendar
  ON calendar.description = detail.calendar_label
ORDER BY
  patient_id,
  calendar_id,
  start_time,
  end_time,
  duration,
  status,
  attendance
${OFFSET_PLACEHOLDER}
`;

export type CorePracticePatientAppointmentDetail = ISourceEntityTypes<
  ICorePracticePatientAppointmentDetail,
  ICorePracticePatientAppointmentDetailTranslations,
  ICorePracticePatientAppointmentDetailFilters
>;

export class PatientAppointmentDetailSourceEntity extends BaseSourceEntity<CorePracticePatientAppointmentDetail> {
  sourceEntity = PATIENT_APPOINTMENT_DETAIL_SOURCE_ENTITY;
  entityResourceType = PATIENT_APPOINTMENT_DETAIL_RESOURCE_TYPE;
  sourceQuery = PATIENT_APPOINTMENT_DETAIL_SOURCE_QUERY;
  verifySourceFn = isCorePracticePatientAppointmentDetail;

  /**
   * The joined query is expensive to compute and OFFSET pagination re-runs it
   * for every page. So we fetch them all in a single page to run the
   * join exactly once instead of per page. Publishing
   * is still chunked downstream via bufferCount(50).
   */
  override defaultOffsetSize = 300000;

  override transformDataFn = flow([
    TRANSFORM_DATA_FN_DEFAULTS,
    this._splitTags,
  ]);

  translate(
    data: ICorePracticePatientAppointmentDetail,
    timezone: Timezone
  ): ICorePracticePatientAppointmentDetailTranslations {
    return {
      from: toTimestamp(
        moment.tz(data.startTime, ISO_DATE_TIME_FORMAT, timezone)
      ),
      to: toTimestamp(moment.tz(data.endTime, ISO_DATE_TIME_FORMAT, timezone)),
    };
  }

  getSourceRecordId(data: ICorePracticePatientAppointmentDetail): string {
    const hash = createHash('SHA-256')
      .update(toJSON(serialise(data)))
      .digest('hex');
    return `${data.patientId} - ${data.calendarId} - ${data.startTime} - ${data.endTime} - ${hash}`;
  }

  getSourceLabel(data: ICorePracticePatientAppointmentDetail): string {
    return this.getSourceRecordId(data);
  }

  getFilterData(
    data: ICorePracticePatientAppointmentDetail,
    _timezone: Timezone
  ): ICorePracticePatientAppointmentDetailFilters {
    return {
      patientId: data.patientId,
    };
  }

  private _splitTags(
    records: ICorePracticePatientAppointmentDetail[]
  ): ICorePracticePatientAppointmentDetail[] {
    return records.map((record) => {
      if (record.tags) {
        const tags = isString(record.tags)
          ? record.tags.split(',').map((tag) => tag.trim())
          : record.tags;
        return {
          ...record,
          tags,
        };
      }
      return record;
    });
  }
}
